import { useState, useEffect, useRef, FormEvent, useCallback, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { SCHEDULE_PATH } from "@/constants/links";

// Lazy-load heavy deps only needed after user interaction
const ReactMarkdown = lazy(() => import("react-markdown").then(m => ({ default: m.default })));

const words = ["Accelerate", "Modernize", "Outperform"];

const SkeletonLoader = () => {
  return (
    <div className="space-y-4 animate-pulse">
      {/* Heading skeleton */}
      <div className="h-5 bg-muted/60 rounded-md w-3/4" />
      {/* Paragraph skeletons */}
      <div className="space-y-2.5">
        <div className="h-3.5 bg-muted/40 rounded w-full" />
        <div className="h-3.5 bg-muted/40 rounded w-[95%]" />
        <div className="h-3.5 bg-muted/40 rounded w-[85%]" />
      </div>
      <div className="space-y-2.5">
        <div className="h-3.5 bg-muted/40 rounded w-full" />
        <div className="h-3.5 bg-muted/40 rounded w-[90%]" />
        <div className="h-3.5 bg-muted/40 rounded w-[70%]" />
      </div>
      {/* Bullet point skeletons */}
      <div className="space-y-2 pl-4">
        <div className="h-3.5 bg-muted/40 rounded w-[80%]" />
        <div className="h-3.5 bg-muted/40 rounded w-[75%]" />
        <div className="h-3.5 bg-muted/40 rounded w-[65%]" />
      </div>
      <div className="space-y-2.5 pt-2">
        <div className="h-3.5 bg-muted/40 rounded w-[88%]" />
        <div className="h-3.5 bg-muted/40 rounded w-[60%]" />
      </div>
      {/* CTA skeleton */}
      <div className="flex justify-center pt-4">
        <div className="h-10 bg-muted/50 rounded-full w-48" />
      </div>
    </div>
  );
};

const HeroSection = () => {
  const [wordIndex, setWordIndex] = useState(0);
  const [query, setQuery] = useState("");
  const [response, setResponse] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const responseRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const loadTimestamp = useRef(Date.now());

  // Honeypot state — hidden from real users
  const [honeypot, setHoneypot] = useState("");

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setWordIndex((prev) => (prev + 1) % words.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  // Scroll search area to top of screen when submitted
  useEffect(() => {
    if (hasSubmitted && sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [hasSubmitted]);
  const handleReset = () => {
    setQuery("");
    setResponse("");
    setHasSubmitted(false);
    setIsLoading(false);
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!query.trim() || isLoading) return;

    setHasSubmitted(true);
    setResponse("");
    setIsLoading(true);

    const timeSinceLoad = Date.now() - loadTimestamp.current;
    let accumulated = "";

    try {
      const { streamChat } = await import("@/lib/streamChat");
      await streamChat({
        messages: [{ role: "user", content: query.trim() }],
        honeypot,
        loadTime: timeSinceLoad,
        onDelta: (chunk) => {
          accumulated += chunk;
        },
        onDone: () => {
          setResponse(accumulated);
          setIsLoading(false);
          // Save query and response to database
          if (accumulated) {
            supabase.
            from("search_queries").
            insert({ query: query.trim(), response: accumulated }).
            select("id").
            single().
            then(({ data, error }) => {
              if (error) console.error("Failed to save search query:", error);
              else if (data?.id) {
                // Fire-and-forget categorization
                supabase.functions.invoke("categorize-query", {
                  body: { queryId: data.id, queryText: query.trim() },
                }).catch((err) => console.error("Categorization failed:", err));
              }
            });
          }
        },
        onError: (error) => {
          toast.error(error);
          setIsLoading(false);
        }
      });
    } catch {
      toast.error("Failed to connect. Please try again.");
      setIsLoading(false);
    }
  };

  return (
    <section ref={sectionRef} className="relative min-h-screen flex items-start justify-center overflow-hidden pt-[15vh] sm:pt-[25vh]">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,hsl(221_83%_46%/0.04),transparent_50%)]" />

      <div className="container mx-auto px-6 text-center relative z-10 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}>
          
          {/* Rotating words */}
          {!hasSubmitted && (
            <div className="mb-3 h-[1.1em] text-4xl sm:text-5xl md:text-6xl font-bold leading-tight">
              <AnimatePresence mode="wait">
                <motion.span
                  key={words[wordIndex]}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -30 }}
                  transition={{ duration: 0.4 }}
                  className="block text-primary">
                  {words[wordIndex]}
                </motion.span>
              </AnimatePresence>
            </div>
          )}

          {!hasSubmitted && (
            <p className="text-lg sm:text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto leading-relaxed mb-10">
              your software with our expert{" "}
              <span className="font-semibold text-foreground">
                AI-advantaged dev teams
              </span>
            </p>
          )}

          {/* Search input - auto-expanding textarea */}
          <form onSubmit={handleSubmit} className={`relative max-w-2xl mx-auto ${hasSubmitted ? 'mt-4' : ''}`}>
            {/* Honeypot field — invisible to real users, bots will fill it */}
            <input
              type="text"
              name="website_url"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0, overflow: "hidden" }}
            />
            <div className="relative group">
              <Search className="absolute left-4 top-4 w-5 h-5 text-muted-foreground group-focus-within:text-primary transition-colors" />
              <textarea
                ref={textareaRef}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  autoResize();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
                disabled={isLoading}
                rows={1}
                className="w-full pl-12 pr-4 py-3 sm:py-4 rounded-2xl border border-border bg-card text-foreground text-base sm:text-lg placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all shadow-sm hover:shadow-md disabled:opacity-60 resize-none overflow-hidden"
                placeholder="What's your TechNeed?" />
            </div>
          </form>

          {!hasSubmitted && (
            <p className="mt-4 text-sm text-muted-foreground">
              or{" "}
              <Link
                to={SCHEDULE_PATH}
                className="text-primary hover:text-primary/80 underline underline-offset-4 transition-colors"
              >
                schedule a conversation →
              </Link>
            </p>
          )}
        </motion.div>

        {/* Response area - no scroll container, full content visible */}
        {hasSubmitted && (
          <div className="mt-8">
            <div
              ref={responseRef}
              className="max-w-2xl mx-auto text-left bg-card border border-border rounded-2xl p-4 sm:p-6 shadow-sm">
              
              {isLoading && !response && <SkeletonLoader />}
              <AnimatePresence>
                {response && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                    className="prose prose-sm max-w-none text-foreground prose-headings:text-foreground prose-p:text-muted-foreground prose-strong:text-foreground prose-li:text-muted-foreground prose-blockquote:text-muted-foreground"
                  >
                    <Suspense fallback={<div className="animate-pulse h-20 bg-muted/40 rounded" />}>
                    <ReactMarkdown
                      components={{
                        a: ({ href, children }) => {
                          const isCalendly = href?.includes("calendly.com");
                          if (isCalendly) {
                            return (
                              <span className="not-prose flex justify-center mt-6 mb-2">
                                <a
                                  href={href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground px-8 py-3 text-sm font-medium shadow-sm hover:bg-primary/90 transition-colors"
                                >
                                  {children}
                                </a>
                              </span>
                            );
                          }
                          const isInternal = href?.startsWith("/");
                          return (
                            <a
                              href={href}
                              {...(!isInternal && { target: "_blank", rel: "noopener noreferrer" })}
                              className="text-primary underline underline-offset-4 hover:text-primary/80 transition-colors"
                            >
                              {children}
                            </a>
                          );
                        },
                      }}
                    >
                      {response}
                    </ReactMarkdown>
                    </Suspense>
                  </motion.div>
                )}
              </AnimatePresence>
              {response && !isLoading && (
                <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Link
                    to={SCHEDULE_PATH}
                    className="inline-flex items-center justify-center rounded-full border border-primary text-primary px-6 py-2.5 text-sm font-medium hover:bg-primary hover:text-primary-foreground transition-colors"
                  >
                    Schedule a meeting
                  </Link>
                  <button
                    onClick={handleReset}
                    className="inline-flex items-center justify-center rounded-full border border-border text-muted-foreground px-6 py-2.5 text-sm font-medium hover:bg-accent hover:text-foreground transition-colors"
                  >
                    Ask another question
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );

};

export default HeroSection;
